<template>
  <div
    :class="[
      `flex min-w-0 grow select-none items-center text-sm`,
      !hasValue && `text-muted-foreground`,
    ]"
    @click="handleContainerClick"
    @focusout="handleFocusOut"
  >
    <template v-for="(part, index) in renderedParts" :key="index">
      <span v-if="part.kind === 'literal'" class="text-muted-foreground">
        {{ part.value }}
      </span>
      <TimeSegment
        v-else-if="part.kind === 'segment'"
        ref="inputs"
        :type="part.type"
        :value="part.data.value"
        :min="part.data.min"
        :max="part.data.max"
        :aria-value-text="part.data.ariaValueText"
        :disabled="disabled"
        @change="part.onChange"
        @increment="part.onIncrement"
        @next="part.onNext"
        @prev="part.onPrev"
      />
      <DayPeriodSegment
        v-else-if="part.kind === 'dayPeriod'"
        ref="inputs"
        :value="segments.dayPeriod"
        :am-label="amLabel"
        :pm-label="pmLabel"
        :disabled="disabled"
        @change="handleDayPeriodChange"
        @next="focusNextFrom('dayPeriod')"
        @prev="focusPrevFrom('dayPeriod')"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import { mapDefined } from "@/lib/collections";
import {
  getDayPeriodLabels,
  getHourCycle,
  getLocaleTimeParts,
  isValidISOTime,
} from "@/lib/time";

import DayPeriodSegment from "./DayPeriodSegment.vue";
import TimeSegment from "./TimeSegment.vue";

type SegmentType = "dayPeriod" | "hour" | "minute";

const modelValue = defineModel<string>({ default: "" });

defineProps<{
  disabled?: boolean;
}>();

const inputs = useTemplateRef("inputs");

const hourCycle = computed(() => getHourCycle());
const is12h = computed(
  () => hourCycle.value === "h12" || hourCycle.value === "h11"
);

const segments = ref<{
  hour: number | null;
  minute: number | null;
  dayPeriod: number | null;
}>({
  hour: null,
  minute: null,
  dayPeriod: null,
});

const hasValue = computed(() => {
  return segments.value.hour !== null && segments.value.minute !== null;
});

// Hour cycle conversion
function toDisplayHour(hour24: number): [number, number] {
  if (is12h.value) {
    const period = hour24 >= 12 ? 1 : 0;
    const h = hour24 % 12 || 12;
    return [h, period];
  }
  return [hour24, 0];
}

function to24Hour(displayHour: number, dayPeriod: number): number {
  if (is12h.value) {
    const base = displayHour === 12 ? 0 : displayHour;
    return dayPeriod === 1 ? base + 12 : base;
  }
  return displayHour;
}

const { am: amLabel, pm: pmLabel } = getDayPeriodLabels();

// Segment min/max
const hourMin = computed(() => (is12h.value ? 1 : 0));
const hourMax = computed(() => (is12h.value ? 12 : 23));

interface FormatPart {
  type: "dayPeriod" | "hour" | "literal" | "minute";
  value: string;
}

const formatParts = computed((): FormatPart[] => {
  return getLocaleTimeParts() as FormatPart[];
});

const segmentOrder = computed((): SegmentType[] => {
  return mapDefined(formatParts.value, (p) => {
    if (p.type === "hour" || p.type === "minute" || p.type === "dayPeriod") {
      return p.type;
    }
  });
});

interface NumericSegmentData {
  value: number | null;
  min: number;
  max: number;
  ariaValueText?: string;
}

interface RenderedSegment {
  kind: "segment";
  type: "hour" | "minute";
  data: NumericSegmentData;
  onChange: (value: number | null) => void;
  onIncrement: (delta: number) => void;
  onNext: () => void;
  onPrev: () => void;
}

interface RenderedDayPeriod {
  kind: "dayPeriod";
}

interface RenderedLiteral {
  kind: "literal";
  value: string;
}

type RenderedPart = RenderedDayPeriod | RenderedLiteral | RenderedSegment;

const renderedParts = computed((): RenderedPart[] => {
  return formatParts.value.map((part) => {
    if (part.type === "literal") {
      return { kind: "literal", value: part.value };
    }

    if (part.type === "dayPeriod") {
      return { kind: "dayPeriod" };
    }

    const type = part.type as "hour" | "minute";

    return {
      kind: "segment",
      type,
      data: getSegmentData(type),
      onChange: (value) => handleSegmentChange(type, value),
      onIncrement: (delta) => handleSegmentIncrement(type, delta),
      onNext: () => focusNextFrom(type),
      onPrev: () => focusPrevFrom(type),
    };
  });
});

const getSegmentData = (type: "hour" | "minute"): NumericSegmentData => {
  const value = segments.value[type];
  switch (type) {
    case "hour": {
      const ariaText =
        value !== null && is12h.value
          ? `${value} ${segments.value.dayPeriod === 1 ? pmLabel : amLabel}`
          : undefined;
      return {
        value,
        min: hourMin.value,
        max: hourMax.value,
        ariaValueText: ariaText,
      };
    }
    case "minute": {
      return { value, min: 0, max: 59 };
    }
  }
};

const getSegmentIndex = (type: SegmentType): number => {
  // Inputs ref collects both TimeSegment and DayPeriodSegment in DOM order
  const order = segmentOrder.value;
  return order.indexOf(type);
};

const focusSegmentByIndex = (index: number): void => {
  const refs = inputs.value;
  if (!refs) return;
  (refs[index] as { focus: () => void })?.focus();
};

const focusSegment = (type: SegmentType): void => {
  const index = getSegmentIndex(type);
  if (index !== -1) {
    focusSegmentByIndex(index);
  }
};

const focusFirstSegment = (): void => {
  const first = segmentOrder.value[0];
  if (first) focusSegment(first);
};

const focusLastSegment = (): void => {
  const last = segmentOrder.value[segmentOrder.value.length - 1];
  if (last) focusSegment(last);
};

const focusNextFrom = (type: SegmentType): void => {
  const order = segmentOrder.value;
  const i = order.indexOf(type);
  if (i < order.length - 1) {
    focusSegment(order[i + 1]);
  }
};

const focusPrevFrom = (type: SegmentType): void => {
  const order = segmentOrder.value;
  const i = order.indexOf(type);
  if (i > 0) {
    focusSegment(order[i - 1]);
  }
};

const handleContainerClick = (event: MouseEvent): void => {
  const target = event.target as Element;
  if (target.closest("[data-segment]")) return;
  focusFirstSegment();
};

const handleFocusOut = (event: FocusEvent): void => {
  const container = event.currentTarget as Element;
  const relatedTarget = event.relatedTarget as Element | null;
  if (relatedTarget && container.contains(relatedTarget)) return;
  commitValue();
};

const commitValue = (): void => {
  clampSegments();
  updateModelValue();
};

const handleSegmentChange = (
  type: "hour" | "minute",
  value: number | null
): void => {
  segments.value[type] = value;
  if (
    type === "hour" &&
    value !== null &&
    is12h.value &&
    segments.value.dayPeriod === null
  ) {
    segments.value.dayPeriod = 0;
  }
  updateModelValue();
};

const handleDayPeriodChange = (value: number): void => {
  segments.value.dayPeriod = value;
  updateModelValue();
};

const handleSegmentIncrement = (
  type: "hour" | "minute",
  delta: number
): void => {
  const current = segments.value[type];

  switch (type) {
    case "hour": {
      const min = hourMin.value;
      const max = hourMax.value;
      if (current === null) {
        segments.value.hour = delta > 0 ? min : max;
      } else {
        let next = current + delta;
        if (next < min) next = max;
        if (next > max) next = min;
        segments.value.hour = next;
      }
      if (is12h.value && segments.value.dayPeriod === null) {
        segments.value.dayPeriod = 0;
      }
      break;
    }
    case "minute": {
      if (current === null) {
        segments.value.minute = delta > 0 ? 0 : 59;
      } else {
        let next = current + delta;
        if (next < 0) next = 59;
        if (next > 59) next = 0;
        segments.value.minute = next;
      }
      break;
    }
  }

  updateModelValue();
};

const clampSegments = (): void => {
  const { hour, minute } = segments.value;
  const min = hourMin.value;
  const max = hourMax.value;

  if (hour !== null) {
    if (hour < min) segments.value.hour = min;
    if (hour > max) segments.value.hour = max;
  }

  if (minute !== null) {
    if (minute < 0) segments.value.minute = 0;
    if (minute > 59) segments.value.minute = 59;
  }
};

let snapshot: string | undefined;

const updateModelValue = (): void => {
  const { hour, minute, dayPeriod } = segments.value;

  if (hour === null && minute === null) {
    snapshot = "";
    modelValue.value = "";
    return;
  }

  if (hour === null || minute === null) {
    return;
  }

  const hour24 = to24Hour(hour, dayPeriod ?? 0);
  const next = `${hour24.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;

  if (next !== modelValue.value) {
    snapshot = next;
    modelValue.value = next;
  }
};

watch(
  modelValue,
  (value) => {
    if (snapshot === value) return;
    snapshot = value;

    if (!value) {
      segments.value = { hour: null, minute: null, dayPeriod: null };
      return;
    }

    if (!isValidISOTime(value)) return;

    const [h, m] = value.split(":").map(Number);
    const [displayHour, period] = toDisplayHour(h);
    segments.value = {
      hour: displayHour,
      minute: m,
      dayPeriod: is12h.value ? period : null,
    };
  },
  { immediate: true }
);

defineExpose({
  focusFirstSegment,
  focusLastSegment,
});
</script>
